import { Description } from '@mui/icons-material';
import { Box, Chip, Grid, Skeleton, Typography } from '@mui/material';
import { getTemplateDocumentFile } from 'lib/api/integrations/digitalSignature';
import DocumentsPreviewer from 'lib/common/DocumentsPreviewer';
import { SignatureType, SignDocumentTemplate } from 'lib/types/models';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

const TemplateDocument: React.FC<{ doc: SignDocumentTemplate }> = ({ doc }) => {
  const dispatch = useDispatch();
  const [openPreview, setOpenPreview] = useState(false);
  const { data, isPending, error } = getTemplateDocumentFile(dispatch, doc.id);

  const url = useMemo(() => {
    if (data) return URL.createObjectURL(data) + '#pagemode=none';
    return null;
  }, [data]);

  const contentType = useMemo(() => {
    if (data) return data.type;
    return null;
  }, [data]);

  const errorMessage = useMemo(() => {
    if (error) return error.message;
    return null;
  }, [error]);

  const getSignatureTypeLabel = (signature: SignatureType) => {
    switch (signature) {
      case SignatureType.FEA:
        return 'FEA';
      case SignatureType.FES:
        return 'FES';
      default:
        return signature;
    }
  };

  if (isPending) return <SkeletonDocument id={doc.id} />;

  return (
    <>
      <Grid item xs={12} key={doc.id}>
        <Box
          onClick={() => !isPending && setOpenPreview(true)}
          sx={{
            p: 0.75,
            borderRadius: 1,
            border: '1px solid #E5E7EB',
            backgroundColor: 'white',
            cursor: !isPending ? 'pointer' : 'default',
            transition: 'box-shadow 0.2s',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: !isPending ? '0px 2px 8px rgba(0,0,0,0.07)' : 'none',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  borderRadius: '6px',
                  p: 0.4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Description sx={{ fontSize: 14, color: 'error.main' }} />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.7rem' }}
                >
                  Documento
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}
                >
                  {doc.description || 'Sin descripción'}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={getSignatureTypeLabel(doc.signature)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.6rem' }}
            />
          </Box>
        </Box>
      </Grid>
      <DocumentsPreviewer
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        fileName={doc.description ?? 'Documento'}
        blobUrl={url}
        contentType={contentType}
        loading={isPending}
        error={errorMessage}
      />
    </>
  );
};

const SkeletonDocument = ({ id }: { id: string }) => {
  return (
    <Grid item xs={12} key={id}>
      <Box
        sx={{
          p: 0.75,
          borderRadius: 1,
          border: '1px solid #E5E7EB',
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <Skeleton
              variant="rectangular"
              width={24}
              height={24}
              sx={{ borderRadius: '6px' }}
            />
            <Box>
              <Skeleton
                variant="text"
                width={80}
                height={14}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width={120} height={12} />
            </Box>
          </Box>
          <Skeleton
            variant="rounded"
            width={50}
            height={18}
            sx={{ borderRadius: '12px' }}
          />
        </Box>
      </Box>
    </Grid>
  );
};

export default TemplateDocument;
